import React from 'react';
import { AutomaticFormProvider } from './context/AutomaticFormContext.tsx';
import { PositionAutomaticProductsForm } from './modules/PositionAutomaticProductsForm.tsx';
import { OrderAutomaticProductsForm } from './modules/OrderAutomaticProductsForm.tsx';
import { ProductConfigurationScope } from '@/shared/types/ProductConfigurationScope.ts';
import { AUTOMATIC_SUPPORTED_UNITS, BusinessCase, PRICING_STRATEGY, PRODUCT_TYPES } from '@schuettflix/interfaces';
import { ProductConfiguration } from '@/shared/models/ContextDataModel/interfaces/entities/product-configuration.ts';
import {
    AutomaticProductsFormValues,
    PartialAutomaticProductsFormValues,
} from '@/shared/forms/AutomaticProductsForm/schema/getAutomaticProductsFormSchema.ts';
import { AdditionalServiceFormRule } from '@/shared/forms/AutomaticProductsForm/schema/getAdditionalServiceSchema.ts';
import { useOrganizationNames } from './hooks/useOrganizationNames.ts';
import { InlineLoadingSpinner } from '@/shared/components/InlineLoadingSpinner.tsx';
import { useLegacyFactoryId } from '@/clients/location/useLocations.ts';
import { prefillLegacyFactoryId } from './domain/prefillLegacyFactoryId.ts';

export type AutomaticProductsFormRules = {
    isSelectionClearingDisabled?: boolean;
    pricingStrategy?: {
        disabled?: boolean;
    };
    freightType?: {
        disabled?: boolean;
    };
    disposalPurpose?: {
        disabled?: boolean;
    };
    salesMaterialQuantity?: {
        minValue?: number;
        unit?: AUTOMATIC_SUPPORTED_UNITS;
    };
    salesTransportQuantity?: {
        minValue?: number;
        unit?: AUTOMATIC_SUPPORTED_UNITS;
    };
    salesMaterialUnit?: {
        disabled?: boolean;
    };
    salesTransportUnit?: {
        disabled?: boolean;
    };
    purchaseMaterialUnit?: {
        disabled?: boolean;
    };
    purchaseTransportUnit?: {
        disabled?: boolean;
    };
    salesMaterialTaxClassId?: {
        disabled?: boolean;
    };
    salesTransportTaxClassId?: {
        disabled?: boolean;
    };
    additionalServices?: {
        [identifier: string]: AdditionalServiceFormRule;
    };
};

type SubmitPayload = {
    values: AutomaticProductsFormValues;
    businessCase: BusinessCase;
    productConfigurations: ProductConfiguration[];
};

export type AutomaticProductsFormProps = {
    scope: ProductConfigurationScope;
    productType: PRODUCT_TYPES;
    isStandalone: boolean;
    pricingStrategy?: PRICING_STRATEGY;
    constructionProjectId: string;
    customerOrganizationId: number;
    defaultValues?: PartialAutomaticProductsFormValues;
    onSubmit: (payload: SubmitPayload) => void;
    onClearSelection: () => Promise<void> | void;
    formRules?: AutomaticProductsFormRules;
    mode: 'create' | 'edit';
};

export const AutomaticProductsForm: React.FC<AutomaticProductsFormProps> = ({
    constructionProjectId,
    customerOrganizationId,
    scope,
    formRules,
    onClearSelection,
    ...props
}) => {
    const { supplierOrganizationName, carrierOrganizationName, additionalServices, isLoading } = useOrganizationNames({
        supplierOrganizationId: props.defaultValues?.supplierOrganizationId,
        carrierOrganizationId: props.defaultValues?.carrierOrganizationId,
        additionalServices: props.defaultValues?.additionalServices,
    });

    const { data: legacyFactoryId, isLoading: isLegacyFactoryIdLoading } = useLegacyFactoryId(
        props.defaultValues?.loadingLocation?.location
    );

    if (isLoading || isLegacyFactoryIdLoading) {
        return <InlineLoadingSpinner />;
    }

    const defaultValues = prefillLegacyFactoryId(props.defaultValues, legacyFactoryId);

    return (
        <AutomaticFormProvider
            constructionProjectId={constructionProjectId}
            customerOrganizationId={customerOrganizationId}
            formRules={formRules}
            onClearSelection={onClearSelection}
            defaultValuesLabels={{
                supplierOrganization: supplierOrganizationName,
                carrierOrganization: carrierOrganizationName,
                additionalServices,
            }}
        >
            {scope === 'ORDER' ? (
                <OrderAutomaticProductsForm {...props} defaultValues={defaultValues} />
            ) : (
                <PositionAutomaticProductsForm {...props} defaultValues={defaultValues} />
            )}
        </AutomaticFormProvider>
    );
};
